import { format, differenceInDays, isToday, isTomorrow, isPast, isYesterday, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameMonth, isSameWeek, isWithinInterval, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Formate une date pour la base de données en utilisant le fuseau horaire local
 * Évite les problèmes de décalage causés par toISOString() qui utilise UTC
 */
export function formatDateForDB(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getSmartDateDisplay(dateString: string | null | undefined): string | null {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  const today = new Date();
  const daysDiff = differenceInDays(date, today);
  
  if (isPast(date) && !isToday(date)) {
    if (isYesterday(date)) {
      return "Hier";
    } else if (daysDiff === -2) {
      return "Avant-hier";
    } else {
      return `Il y a ${Math.abs(daysDiff)} jours`;
    }
  } else if (isToday(date)) {
    return "Aujourd'hui";
  } else if (isTomorrow(date)) {
    return "Demain";
  } else if (daysDiff === 2) {
    return "Après-demain";
  } else if (daysDiff <= 5) {
    return `Dans ${daysDiff} jours`;
  } else {
    return format(date, 'PPP', { locale: fr });
  }
}

// Fonction pour grouper les événements par mois
export function groupEventsByMonth(events: any[]) {
  const groups = new Map<string, any[]>();
  
  events.forEach(event => {
    const date = new Date(event.start_date);
    const monthKey = format(date, 'yyyy-MM');
    
    if (!groups.has(monthKey)) {
      groups.set(monthKey, []);
    }
    groups.get(monthKey)!.push(event);
  });
  
  return groups;
}

// Fonction pour grouper les événements du mois courant par semaine
export function groupCurrentMonthByWeek(events: any[]) {
  const today = new Date();
  const currentMonth = startOfMonth(today);
  const endOfCurrentMonth = endOfMonth(today);
  
  // Filtrer les événements du mois courant
  const currentMonthEvents = events.filter(event => {
    const eventDate = new Date(event.start_date);
    return isSameMonth(eventDate, today);
  });
  
  // Grouper par semaine
  const thisWeek = [];
  const nextWeek = [];
  const laterThisMonth = [];
  
  const startOfThisWeek = startOfWeek(today, { weekStartsOn: 1 }); // Lundi
  const endOfThisWeek = endOfWeek(today, { weekStartsOn: 1 });
  const startOfNextWeek = addDays(endOfThisWeek, 1);
  const endOfNextWeek = endOfWeek(startOfNextWeek, { weekStartsOn: 1 });
  
  currentMonthEvents.forEach(event => {
    const eventDate = new Date(event.start_date);
    
    if (isWithinInterval(eventDate, { start: startOfThisWeek, end: endOfThisWeek })) {
      thisWeek.push(event);
    } else if (isWithinInterval(eventDate, { start: startOfNextWeek, end: endOfNextWeek })) {
      nextWeek.push(event);
    } else {
      laterThisMonth.push(event);
    }
  });
  
  return {
    thisWeek: thisWeek.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()),
    nextWeek: nextWeek.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()),
    laterThisMonth: laterThisMonth.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
  };
}

// Fonction pour déterminer si on doit grouper les événements de début du mois suivant
export function shouldGroupWithNextMonth(events: any[]) {
  const today = new Date();
  const dayOfMonth = today.getDate();
  const daysLeftInMonth = endOfMonth(today).getDate() - dayOfMonth;
  
  // Si on est dans les 10 derniers jours du mois
  if (daysLeftInMonth <= 10) {
    const nextMonth = addDays(endOfMonth(today), 1);
    const firstWeekOfNextMonth = endOfWeek(nextMonth, { weekStartsOn: 1 });
    
    // Vérifier s'il y a des événements dans la première semaine du mois suivant
    const nextMonthEarlyEvents = events.filter(event => {
      const eventDate = new Date(event.start_date);
      return isWithinInterval(eventDate, { start: nextMonth, end: firstWeekOfNextMonth });
    });
    
    return nextMonthEarlyEvents.length > 0;
  }
  
  return false;
}

// Fonction pour filtrer les événements passés
export function getPastEvents(events: any[]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Début de la journée
  
  return events
    .filter(event => {
      const eventDate = new Date(event.start_date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate < today;
    })
    .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()); // Plus récents en premier
}

// Fonction pour filtrer les événements à venir (y compris aujourd'hui)
export function getUpcomingEvents(events: any[]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Début de la journée
  
  return events
    .filter(event => {
      const eventDate = new Date(event.start_date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    })
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()); // Plus proches en premier
}

// Fonction pour obtenir le libellé d'un groupe d'événements
export function getEventGroupLabel(monthKey: string, isCurrentMonth: boolean, weekType?: 'thisWeek' | 'nextWeek' | 'laterThisMonth') {
  if (isCurrentMonth && weekType) {
    switch (weekType) {
      case 'thisWeek':
        return 'Cette semaine';
      case 'nextWeek':
        return 'La semaine prochaine';
      case 'laterThisMonth':
        return 'Plus tard ce mois-ci';
    }
  }
  
  const date = new Date(monthKey + '-01');
  return format(date, 'MMMM yyyy', { locale: fr });
}